<script>
  import Row from "./Row.svelte";
  import Modal from "./Modal.svelte";
  import raw from "@data/measures.json";
  import Scrolly from "@helpers/Scrolly.svelte";
  import Emoji from "@helpers/Emoji.svelte";
  import ButtonContainer from "@helpers/ButtonContainer.svelte";
  import DepartmentWithTooltip from "@helpers/DepartmentWithTooltip.svelte";

  export let isScrollytelling;

  let data = raw.filter(
    (d) =>
      d.Measure != "Revenues Adopted Budget vs Actual Utilization" &&
      d.Measure != "Expenditures Adopted Budget vs Actual Utilization" &&
      d.Measure !=
        "Citywide General Fund Expenditures Adopted Budget vs Actual Utilization"
  );

  import { rollups } from "d3-array";
  const lengths = rollups(
    data,
    (v) => v.length,
    (d) => d.Department
  );

  $: departments = lengths
    .sort((a, b) => b[1] - a[1])
    .sort(
      (a, b) =>
        $topFourDepartments.map((d) => d.Department).includes(b[0]) -
        $topFourDepartments.map((d) => d.Department).includes(a[0])
    )
    .map((d) => d[0]);

  let active = null;

  let matchingOutcomes = [];
  let searchIsEntered = false;

  let step;

  $: highlightedDepartment = departments[0];

  import {
    topFourDepartments,
    selectedPerformanceMeasures,
  } from "@stores/userData.js";

  let buttonText = "Submit";
  import navigateToHash from "@helpers/navigateToHash.js";
  let submit = () => {
    if ($selectedPerformanceMeasures.length == 0) return;

    buttonText = "Let's share!";
    navigateToHash("share", 500);
  };

  import touchscreen from "@stores/touchscreen.js";
  let userHasClosedAtLeastOnce = false;
  import { fly, fade } from "svelte/transition";

  import viewport from "@stores/viewport.js";
  $: placeholderText =
    $viewport.width < 568
      ? "Search an outcome (e.g. trash, air quality, EMS...)"
      : "Search for something you care about (e.g. trash, air quality, EMS...)";
</script>

{#if data}
  {#if isScrollytelling}
    <!-- {#if step === 0}<span in:fly={{ y: 100 }} class="scroll-helper"
        >Scroll Down <Emoji
          emoji="point-down"
          styles="width: 38px; margin-left: 6px;"
        />
      </span>{/if} -->
    {#key $topFourDepartments.map((d) => d.Department).join(", ")}
      <div class="sticky-container">
        <!-- CHART -->
        <div class="grid scrollytelling">
          <!-- No matter the step, we want to render all of the grid items, but hide their elements via css -->
          <!-- This way, there is layout constancy between different steps -->
          {#each departments as department, index}
            <Row
              data={data.filter((d) => d.Department == department)}
              isHidden={(isScrollytelling && typeof step !== "number") ||
                (step < 2 && department != highlightedDepartment) ||
                (step == 2 &&
                  !$topFourDepartments
                    .map((d) => d.Department)
                    .includes(department))}
              {step}
              {department}
              {matchingOutcomes}
              {searchIsEntered}
              {index}
              {isScrollytelling}
              bind:active
            />
          {/each}
        </div>
        <!-- TEXT -->
        <Scrolly bind:value={step}>
          <div class="step" class:active={step === 0}>
            <div class="step-content">
              <p>
                We think that <strong>budgets</strong> should be based on
                <strong class="with-tooltip"
                  ><span class="tooltip text"
                    >What the department wishes to achieve and the measurable
                    results achieved after a period of time.</span
                  >outcomes</strong
                >, so let's review the outcomes in Houston's budget.
              </p>
              <p>
                Here <span class="rect" /> is an outcome. This is from your
                selected department,
                <strong
                  ><DepartmentWithTooltip
                    departmentName={highlightedDepartment}
                  />.</strong
                >
              </p>
              <p
                style="text-align: center; font-size: 1rem; text-transform: uppercase; font-weight: 500;"
              >
                (scroll down &darr;)
              </p>
            </div>
          </div>
          <div class="step" class:active={step === 1}>
            <div class="step-content">
              <p>
                <strong>{highlightedDepartment}</strong> has more than just one
                outcome, though. There are
                <strong
                  >{data.filter((d) => d.Department == highlightedDepartment)
                    .length} outcomes</strong
                >
                in the {highlightedDepartment}, each represented by a square
                <span class="rect" />
              </p>
            </div>
          </div>
          <div class="step" class:active={step === 2}>
            <div class="step-content">
              <p>
                All four of the departments you selected have their own reported
                outcomes. Select a square to read more about that outcome.
              </p>
            </div>
          </div>
          <div class="step" class:active={step === 2}>
            <div class="step-content">
              <p>
                And there are more departments beyond the ones you selected.
              </p>
              <p>
                Here is every "outcome" listed in the Houston budget, each
                represented by a square <span class="rect" />
              </p>
              <p>
                We'll let you explore all of them later. For now, explore the
                outcomes among your four selected departments.
              </p>
            </div>
          </div>
          <div class="step buffer" />
        </Scrolly>
      </div>
    {/key}
  {:else}
    <p class="body-text centered">
      Remember this, from earlier? Now, you can explore <strong>every</strong>
      outcome in the budget, and select which ones you're most passionate about.
      <!-- You can then share these preferences below! -->
    </p>

    <p class="body-text centered">
      Each square <span class="rect" /> represents one
      <strong>outcome.</strong>
    </p>

    <p class="direction-text">
      Select any outcomes you are passionate about by clicking on them below.
      <br />You only need to select one to continue, but you can select as many
      as you want!
    </p>

    <!-- SEARCH BOX -->
    <input
      type="text"
      class="search"
      placeholder={placeholderText}
      on:input={(e) => {
        matchingOutcomes = e.target.value
          ? data.filter((item) => {
              return (item.Keywords + " " + item.Division)
                .toLowerCase()
                .includes(e.target.value.toLowerCase());
            })
          : [];
        searchIsEntered = e.target.value.length > 0;
      }}
    />

    <!-- CHART -->
    <div class="grid">
      {#each departments as department, index}
        <Row
          data={data.filter((d) => d.Department == department)}
          isHidden={false}
          {step}
          {department}
          {matchingOutcomes}
          {searchIsEntered}
          {index}
          {isScrollytelling}
          bind:active
        />
      {/each}
    </div>
    <ButtonContainer
      disabled={$selectedPerformanceMeasures.length == 0}
      disabledReason={`Please select at least one measure.`}
    >
      <button
        on:click={submit}
        disabled={$selectedPerformanceMeasures.length == 0}
        class:disabled={$selectedPerformanceMeasures.length == 0}
      >
        {buttonText}
      </button>
    </ButtonContainer>
  {/if}
{/if}

{#if active}
  <Modal
    {isScrollytelling}
    bind:data={active}
    allData={data.filter((d) => d.Department === active.Department)}
  />
  {#if $touchscreen}
    <div
      class="click-out-listener"
      on:click={() => {
        active = null;
        userHasClosedAtLeastOnce = true;
      }}
    />
    {#if !userHasClosedAtLeastOnce}
      <span transition:fly={{ y: -20 }} class="close-out-text"
        >(Tap anywhere above to close)</span
      >
    {/if}
  {/if}
{/if}

<style>
  .click-out-listener {
    position: fixed;
    top: 0;
    left: 0;
    /* opacity: 0; */
    opacity: 0.01; /* This is absolutely insane, but opacity: 0.01 is necessary to prevent flashing of modal bug */
    background-color: var(--primary-color-base);
    width: 100vw;
    height: 100%;
    z-index: 100;
  }

  .search {
    border: none;
    font-size: 1.25rem;
    font-family: var(--font-serif);
    font-weight: 200;
    width: 100%;
    padding: 0.5rem;
    background: var(--primary-color-base);
    margin-bottom: 1.5rem;
    outline: 1px solid var(--accent-color);
  }

  @media screen and (max-width: 600px) {
    .search {
      margin-top: 0.6rem;
    }
  }

  .grid {
    grid-template-columns: repeat(auto-fit, 150px);
    display: grid;
    grid-template-rows: 1fr;
    justify-content: center;

    width: 100%;
    margin-bottom: 1rem;
  }

  .grid.scrollytelling {
    position: sticky;
    top: calc(var(--nav-height) + 1rem);
  }
  .rect {
    display: inline-block;
    width: 10px;
    height: 10px;
    margin-right: 1px;
    background: var(--accent-color);
    outline: 1px solid black;
    transition: all 300ms ease;
    vertical-align: middle;
  }

  /* @media screen and (max-width: 568px) {
    .grid {
      grid-template-columns: 1fr 1fr;
    }
  } */

  .sticky-container {
    position: relative;
    width: 100%;
    z-index: 5;
    /* height: 100%; */
  }

  .step {
    height: 80vh;
    display: flex;
    place-items: center;
    justify-content: center;
  }

  .step:first-of-type {
    margin-top: -60vh;
  }

  .step-content {
    /* border-radius: 5px; */
    background: whitesmoke;
    color: #ccc;
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    transition: background 500ms ease;
    box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.2);
    z-index: 10;
    border-left: 5px solid transparent;
    max-width: 668px;
    margin: auto 1rem;
  }

  .step.active .step-content {
    background: white;
    color: black;
    border-left: 5px solid var(--accent-color);
  }

  .step-content p {
    font-size: 1.25rem;
    line-height: 1.25;
    color: rgba(var(--text-color-rgb), 0.95);
    font-weight: 300;
  }

  .step-content p:not(:last-child) {
    margin-bottom: 1rem;
  }

  .step.buffer {
    height: 60vh;
  }

  @media screen and (max-width: 568px) {
    .sticky-container {
      top: var(--nav-height);
      transform: none;
    }

    .grid {
      grid-template-columns: repeat(auto-fit, 140px);
    }
  }

  @media screen and (max-width: 468px) {
    .grid {
      grid-template-columns: repeat(auto-fit, 120px);
    }
  }

  .close-out-text {
    z-index: 10000;
    position: fixed;
    top: 0.5rem;
    right: 0.5rem;
    background: white;
    color: black;
    padding: 0.5rem;
    box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.25);
    border-radius: 3px;
    font-family: var(--font-serif);
    font-weight: 300;
    font-size: 0.85rem;
    pointer-events: none;
  }

  /* .scroll-helper {
    position: fixed;
    bottom: 3.5rem;
    left: 50%;
    text-transform: uppercase;
    font-weight: 500;
    transform: translateX(-50%);
    font-size: 2rem;
    font-family: var(--font-serif);
    text-align: center;
    width: 100%;
  } */
</style>
